import type { AnswerOption } from '../../domain/entities/answer.entity';
import { RichText } from '@/shared/rich-text/RichText';
import { toParagraphs } from '@/shared/rich-text/rich-text';
import type { SimuladoQuestion } from '../../domain/entities/question.entity';

interface QuestionCardProps {
  question: SimuladoQuestion;
  position: { index: number; total: number };
  /** A resposta gravada, ou `undefined` se a questão está em branco. */
  selected: AnswerOption | undefined;
  /** Clique numa alternativa: quem usa grava e decide se avança. */
  onPick: (answer: AnswerOption) => void;
  onPrevious: () => void;
  onNext: () => void;
}

/**
 * A questão atual: enunciado, alternativas e a navegação anterior / próxima.
 * O enunciado e as alternativas são os do card do `enamed-feedback-lp`.
 *
 * Sem botão de confirmar: **a alternativa clicada é a resposta** (ver
 * `QuestionStepper`). A dica abaixo das alternativas diz isso, e diz como
 * deixar em branco — sem ela, desmarcar seria um gesto escondido.
 */
export function QuestionCard({
  question,
  position,
  selected,
  onPick,
  onPrevious,
  onNext,
}: QuestionCardProps) {
  const isFirst = position.index === 0;
  const isLast = position.index === position.total - 1;

  return (
    <article
      className={`qcard ${selected ? 'is-marked' : 'is-blank'}`}
      aria-labelledby={`question-${question.number}-title`}
    >
      <div className="qcard-head">
        <h2 className="qcard-number" id={`question-${question.number}-title`}>
          Questão {question.number}
          <span className="qcard-of"> de {position.total}</span>
        </h2>
        {selected && <span className="qcard-pick">Respondida: {selected}</span>}
      </div>

      {/* Como no ENAMED: o texto pode vir em HTML e trazer imagem embutida
          (tabela, ECG, radiografia) — sem ela a questão pode não ter resposta.
          Só o enunciado: o `complement` da Aristo traz dado de controle
          ("Caderno Único", um número solto), e não texto da questão. */}
      <RichText
        value={question.body}
        className="qcard-body"
        questionNumber={question.number}
      />

      <div
        className="qcard-alts"
        role="group"
        aria-label={`Alternativas da questão ${question.number}`}
      >
        {question.alternatives.map((alternative) => {
          const isSelected = selected === alternative.letter;

          return (
            <button
              key={alternative.letter}
              type="button"
              onClick={() => onPick(alternative.letter)}
              aria-pressed={isSelected}
              aria-label={`Alternativa ${alternative.letter}`}
              className={`alt ${isSelected ? 'is-marked' : ''}`}
            >
              <span className="alt-letter" aria-hidden="true">
                {alternative.letter}
              </span>
              <span>{toParagraphs(alternative.text).join(' ')}</span>
            </button>
          );
        })}
      </div>

      <p className="qcard-hint">
        {isLast
          ? 'Esta é a última questão. Clique na alternativa marcada para deixá-la em branco.'
          : 'Ao escolher, você vai para a próxima questão. Clique na alternativa marcada para deixá-la em branco.'}
      </p>

      <div className="qcard-actions">
        <button
          type="button"
          className="ghost ghost-lg"
          onClick={onPrevious}
          disabled={isFirst}
        >
          Anterior
        </button>
        <button
          type="button"
          className="ghost ghost-lg"
          onClick={onNext}
          disabled={isLast}
        >
          Próxima
        </button>
      </div>
    </article>
  );
}
